import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/Kpi";

const inp = "border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-sky-300";
const lbl = "block text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400 mb-1";

/** Configurazione della sezione "Monitoraggio quarter" (titolo, periodo, indicatori, fonti dati). */
export function MonitoraggioQuarterConfig() {
  const [cfg, setCfg] = useState(null);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { api.get("/monitoraggio-quarter/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;

  const setInd = (i, patch) => setCfg({ ...cfg, indicatori: cfg.indicatori.map((x, k) => (k === i ? { ...x, ...patch } : x)) });
  const toggleFonte = (i, f) => {
    const ind = cfg.indicatori[i];
    setInd(i, { fonti: ind.fonti.includes(f) ? ind.fonti.filter((x) => x !== f) : [...ind.fonti, f] });
  };
  const aggiungi = () => setCfg({ ...cfg, indicatori: [...cfg.indicatori, { id: `ind_${Date.now()}`, titolo: "Nuovo indicatore",
    tipo: "barra", unita: "eur", obiettivo: 0, valore_manuale: null, fonti: [] }] });
  const salva = async () => {
    setSalvando(true);
    try {
      await api.put("/monitoraggio-quarter/config", { titolo: cfg.titolo, dal: cfg.dal, al: cfg.al, indicatori: cfg.indicatori });
      toast.success("Monitoraggio quarter salvato");
    } catch (e) { toast.error(apiError(e)); } finally { setSalvando(false); }
  };

  return (
    <Card className="p-6" data-testid="config-monitoraggio-quarter">
      <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-1">Monitoraggio quarter</div>
      <div className="text-sm text-slate-600 mb-4">Sezione in fondo a Monitoraggio DBE: titolo, periodo, indicatori e dati da pescare dalle dashboard.</div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <label className="md:col-span-2"><span className={lbl}>Titolo sezione</span>
          <input className={inp} value={cfg.titolo} onChange={(e) => setCfg({ ...cfg, titolo: e.target.value })} data-testid="mq-cfg-titolo" /></label>
        <label><span className={lbl}>Dal</span>
          <input type="date" className={inp} value={cfg.dal} onChange={(e) => setCfg({ ...cfg, dal: e.target.value })} data-testid="mq-cfg-dal" /></label>
        <label><span className={lbl}>Al</span>
          <input type="date" className={inp} value={cfg.al} onChange={(e) => setCfg({ ...cfg, al: e.target.value })} data-testid="mq-cfg-al" /></label>
      </div>

      <div className="space-y-4">
        {cfg.indicatori.map((ind, i) => (
          <div key={ind.id} className="rounded-lg border border-slate-200 p-4" data-testid={`mq-cfg-ind-${ind.id}`}>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <label className="md:col-span-2"><span className={lbl}>Titolo</span>
                <input className={inp} value={ind.titolo} onChange={(e) => setInd(i, { titolo: e.target.value })} /></label>
              <label><span className={lbl}>Grafico</span>
                <select className={inp} value={ind.tipo} onChange={(e) => setInd(i, { tipo: e.target.value })}>
                  <option value="tachimetro">Tachimetro</option><option value="barra">Indicatore</option></select></label>
              <label><span className={lbl}>Unità</span>
                <select className={inp} value={ind.unita} onChange={(e) => setInd(i, { unita: e.target.value })}>
                  <option value="eur">Euro</option><option value="pezzi">Pezzi</option></select></label>
              <label><span className={lbl}>Obiettivo</span>
                <input type="number" className={inp} value={ind.obiettivo} onChange={(e) => setInd(i, { obiettivo: e.target.value })} /></label>
              <label><span className={lbl}>Valore manuale</span>
                <input type="number" className={inp} value={ind.valore_manuale ?? ""} placeholder="—"
                  onChange={(e) => setInd(i, { valore_manuale: e.target.value === "" ? null : e.target.value })} /></label>
            </div>
            <div className="mt-3">
              <span className={lbl}>Dati da pescare (si sommano tra loro e al valore manuale)</span>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1">
                {cfg.fonti.filter((f) => f.unita === ind.unita).map((f) => (
                  <label key={f.chiave} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                    <input type="checkbox" className="accent-sky-600" checked={ind.fonti.includes(f.chiave)} onChange={() => toggleFonte(i, f.chiave)} />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>
            <button type="button" onClick={() => setCfg({ ...cfg, indicatori: cfg.indicatori.filter((_, k) => k !== i) })}
              className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 transition-colors duration-200">
              <Trash2 size={12} /> Rimuovi indicatore</button>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button type="button" onClick={aggiungi} className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md border border-slate-300 bg-white hover:border-sky-400 transition-colors duration-200">
          <Plus size={14} /> Aggiungi indicatore</button>
        <button type="button" onClick={salva} disabled={salvando} data-testid="mq-cfg-salva"
          className="text-sm px-4 py-2 rounded-md bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-50 transition-colors duration-200">
          Salva</button>
      </div>
    </Card>
  );
}
